import { kotPrintJobs } from "@/lib/kotPrint";
import { settleKotPrints } from "@/lib/remotePrint";
import { withCopies } from "@/lib/printCopies";
import { sendToPrinter } from "@/lib/printJobs";
import type { QRAcceptPendingOrderResponse } from "@/types/qr-orders";

/** What printing an accept's KOTs on this device came to. */
export interface AcceptKotPrint {
  /** The answer carried KOT data (false: KDS-only routing, nothing to print). */
  hadKot: boolean;
  /** Print jobs for this device's printers (0: no KOT printer here). */
  jobs: number;
  /** Jobs the print service took (fewer than `jobs`: not reachable or not paired). */
  queued: number;
  /** The KOTs this device did not print went to the outlet's tills. */
  remote?: boolean;
}

/**
 * Print the KOTs an accept sent to the kitchen, as Send KOT does: one job per
 * station printer (default KOT printer as fallback). The print service reports
 * its own problems; this says whether every KOT reached it.
 */
export async function printAcceptKots(
  response: Pick<
    QRAcceptPendingOrderResponse,
    "kot_prints" | "kot_escpos_data" | "kot_copies" | "kot_ids"
  >,
  kotPrinterId: string | null | undefined,
): Promise<AcceptKotPrint> {
  const hadKot = !!response.kot_escpos_data || (response.kot_prints?.length ?? 0) > 0;
  const jobs = kotPrintJobs(response, kotPrinterId);
  const tickets = await Promise.all(
    jobs.map((job) =>
      sendToPrinter(withCopies({ printer_id: job.printer_id, data: job.data }, job.copies), {
        label: job.label,
      }).catch(() => ({ accepted: false })),
    ),
  );
  // The KOTs this device printed count as printed; the ones that definitely
  // did not print here (no printer for them, service unreachable or not
  // paired) go to a till; a timeout may have printed and stays here.
  const sentKotIds = [...(response.kot_ids ?? []), ...(response.kot_prints ?? []).map((p) => p.kot_id)];
  const handed = await settleKotPrints(
    sentKotIds,
    jobs.map((job, i) => ({ kot_ids: job.kot_ids, ticket: tickets[i] })),
  );
  return { hadKot, jobs: jobs.length, queued: tickets.filter((t) => t.accepted).length, remote: handed.length > 0 };
}

export interface AcceptedToast {
  title: string;
  description?: string;
  variant?: "destructive";
  /** Stays until closed, like a failed print (src/lib/printJobs.tsx). */
  important?: boolean;
  duration?: number;
}

/**
 * The toast after an accept: the table is yours. When the kitchen needed a
 * printed KOT but this device printed none, it says where to print it (the
 * till's Orders screen, the order's KOTs) and stays until closed, as a failed
 * print does.
 */
export function acceptedToast(tableNumber: string | null | undefined, print: AcceptKotPrint | null): AcceptedToast {
  const title = tableNumber ? `Table ${tableNumber} is yours · sent to kitchen` : "QR order accepted · sent to kitchen";
  if (!print || !print.hadKot) return { title };
  // Handed to a till (no printer here, or this one did not take it).
  if (print.remote) return { title, description: "The till prints its KOT." };
  const sticky = { variant: "destructive" as const, important: true, duration: Infinity };
  if (print.jobs === 0) {
    return { title, description: "No KOT printer on this device. Print it at the till: Orders › the order's KOTs.", ...sticky };
  }
  if (print.queued < print.jobs) {
    return {
      title,
      description: "The KOT didn't print on this device. Print it at the till: Orders › the order's KOTs.",
      ...sticky,
    };
  }
  return { title };
}

/** An Accept that got no answer, then the order left the list: it was probably accepted, maybe without a KOT. */
export function unconfirmedAcceptToast(tableLabel: string): AcceptedToast {
  return {
    title: `${tableLabel} was accepted`,
    description: "Check its KOT printed (History › KOTs).",
    variant: "destructive",
    important: true,
    duration: Infinity,
  };
}

/** No answer to an Accept: it may have gone through. Not "Accept failed". */
export const NO_ANSWER_TITLE = "No answer from the server";
